import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, Home, Settings2, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DropdownMenu, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { useHouseholdStore } from '@/stores/household-store'
import { useAuthStore } from '@/stores/auth-store'

interface HouseholdSelectorProps {
  className?: string
}

export function HouseholdSelector({ className }: HouseholdSelectorProps) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const {
    currentHousehold,
    households,
    viewMode,
    setViewMode,
    setCurrentHousehold,
    loadHouseholds,
  } = useHouseholdStore()

  const safeHouseholds = Array.isArray(households) ? households : []

  useEffect(() => {
    if (user && safeHouseholds.length === 0) {
      void loadHouseholds()
    }
  }, [user, loadHouseholds, safeHouseholds.length])

  const handleViewModeChange = (mode: 'personal' | 'household', household?: any) => {
    setViewMode(mode)
    if (mode === 'household' && household) {
      setCurrentHousehold(household)
    } else if (mode === 'personal') {
      setCurrentHousehold(null)
    }
  }

  const currentLabel = viewMode === 'personal' ? 'Persoonlijk' : currentHousehold?.name || 'Huishouden'
  const currentMeta =
    viewMode === 'personal'
      ? user?.name || 'Jouw privé collectie'
      : `${currentHousehold?.member_count || 0} leden`

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <DropdownMenu
        className="w-full"
        triggerClassName="w-full"
        contentClassName="w-80 max-w-[calc(100vw-2rem)]"
        trigger={
          <div className="flex w-full min-w-0 max-w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left shadow-sm transition hover:border-orange-200 hover:bg-orange-50/40">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full ${
                  viewMode === 'personal' ? 'bg-slate-100 text-slate-700' : 'bg-orange-100 text-orange-700'
                }`}
              >
                {viewMode === 'personal' ? <Home className="h-4 w-4" /> : <Users className="h-4 w-4" />}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-gray-900">{currentLabel}</div>
                <div className="truncate text-xs text-gray-500">{currentMeta}</div>
              </div>
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
          </div>
        }
      >
        <DropdownMenuItem
          onClick={() => handleViewModeChange('personal')}
          icon={<Home className="h-4 w-4" />}
          className={viewMode === 'personal' ? 'bg-orange-50 text-orange-700' : ''}
        >
          <div className="flex w-full items-center justify-between">
            <span className="truncate">Persoonlijke collectie</span>
            {viewMode === 'personal' ? <Check className="h-4 w-4" /> : null}
          </div>
        </DropdownMenuItem>

        {safeHouseholds.length > 0 && <div className="border-t border-gray-200 my-1"></div>}

        {safeHouseholds.map((household) => (
          <DropdownMenuItem
            key={household.id}
            onClick={() => handleViewModeChange('household', household)}
            icon={<Users className="h-4 w-4" />}
            className={
              viewMode === 'household' && currentHousehold?.id === household.id
                ? 'bg-orange-50 text-orange-700'
                : ''
            }
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex min-w-0 flex-col text-left">
                <span className="truncate">{household.name}</span>
                <span className="truncate text-xs text-gray-500">{household.member_count || 0} leden</span>
              </div>
              {viewMode === 'household' && currentHousehold?.id === household.id ? <Check className="h-4 w-4" /> : null}
            </div>
          </DropdownMenuItem>
        ))}

        <div className="border-t border-gray-200 my-1"></div>
        <DropdownMenuItem
          onClick={() => navigate('/instellingen')}
          icon={<Settings2 className="h-4 w-4" />}
          className="text-orange-600 hover:bg-orange-50"
        >
          Huishouden beheren
        </DropdownMenuItem>
      </DropdownMenu>

      {safeHouseholds.length === 0 && user ? <div className="text-xs text-gray-500">Nog geen huishoudens</div> : null}
    </div>
  )
}
